import { easeKbMotion } from './rafScrollTo';
import { kbLog } from './keyboardReframeDebug';
import { markProgrammaticScroll } from './scrollTapGuard';
import {
  parkedScrollers,
  parkedOriginal,
  releaseParkedSpacer,
  writeSpacerPad,
  paddingBottomPx,
  setParkedSettleCancel,
  isParkedSettling,
} from './inlineKeyboardSpacer';

/**
 * Keyboard-close SETTLE for parked inline-guard spacers (founder feedback
 * 2026-10-22, after KAN-930 / RD-DEV-025 shipped: "I don't love how it's not
 * going down again smoothly... creating extra space at the bottom").
 *
 * LOCKED rule (replaces KAN-930's "park until the drawer closes"): when the
 * keyboard is gone, a scroller holding a parked spacer
 *  - STAYS where it is if it can (the spacer sits wholly below the visible
 *    bottom, so dropping it shows nothing), else
 *  - glides down ONLY as far as needed so the last real content sits at the
 *    bottom edge: scrollTop → min(current, naturalMax), naturalMax measured
 *    WITHOUT the spacer — while the spacer shrinks away in the SAME frames on
 *    the keyboard curve (KB_MOTION: 250ms cubic-bezier(0.36, 0.66, 0.04, 1)).
 *
 * Why the two move together: scrollTop eases from→to and the extra padding
 * eases E→0 on the same curve, so the visible blank (from − naturalMax)(1−e)
 * and the hidden range (E − (from − naturalMax))(1−e) both shrink
 * monotonically — the browser never has to clamp scrollTop, so nothing snaps.
 * scrollTop is written BEFORE the padding in every frame for the same reason.
 *
 * A re-focus cancels the glide where it is (`cancelAllSettles` /
 * `addSpacerPadding`'s reclaim); the spacer stays parked with its ORIGINAL
 * padding string, so the next reveal plans on the live range and the next
 * keyboard close settles again. No parked spacer survives a keyboard close.
 */

/** = KB_MOTION's duration (keyboardOverlayStyles.js). */
export const SETTLE_MS = 250;

function settleOne(scroller) {
  const orig = parkedOriginal(scroller);
  const startPad = parseFloat(scroller.style.paddingBottom) || 0; // ours: always px
  if (!scroller.isConnected) {
    releaseParkedSpacer(scroller);
    return;
  }
  const from = scroller.scrollTop;
  // Natural range AND the original's px (a calc() original — Log Job's bar
  // clearance — must glide to ITS px, never to 0 and snap back): measured
  // synchronously with the spacer removed (no paint in between), then put
  // back — re-assert scrollTop in case the read clamped.
  writeSpacerPad(scroller, orig, orig);
  const origPx = paddingBottomPx(scroller);
  const naturalMax = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  writeSpacerPad(scroller, `${startPad}px`, orig);
  scroller.scrollTop = from;
  const extra = startPad - origPx;
  if (extra <= 0) {
    releaseParkedSpacer(scroller);
    return;
  }
  const to = Math.min(from, naturalMax);
  if (from - to < 0.5) {
    // Content stays: the spacer is entirely below the visible bottom.
    releaseParkedSpacer(scroller);
    kbLog('inlineRevealSettle', { moved: 0 });
    return;
  }
  kbLog('inlineRevealSettle', { from: Math.round(from), to: Math.round(to), sp: Math.round(extra) });
  markProgrammaticScroll(scroller, SETTLE_MS + 150);
  const start = Date.now();
  let raf = 0;
  const step = () => {
    const p = Math.min(1, (Date.now() - start) / SETTLE_MS);
    if (p >= 1) {
      raf = 0;
      setParkedSettleCancel(scroller, null);
      releaseParkedSpacer(scroller);
      scroller.scrollTop = to;
      return;
    }
    const e = easeKbMotion(p);
    scroller.scrollTop = from + (to - from) * e;
    writeSpacerPad(scroller, `${origPx + extra * (1 - e)}px`, orig);
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  setParkedSettleCancel(scroller, () => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    kbLog('inlineRevealSettleCancel', {});
  });
}

/**
 * Settle every parked spacer that is not already gliding. Call once the keyboard
 * is gone. `skip`: a page scroller whose glide-back is in flight — it releases
 * its parked spacer after that glide lands instead (inlineRestoreGlide).
 */
export function settleParkedSpacers(skip = null) {
  parkedScrollers().forEach((s) => {
    if (s !== skip && !isParkedSettling(s)) settleOne(s);
  });
}
